// Step 3: the details form. Collects name, email, and an optional note with
// zod-mirrored client validation, then submits POST /api/bookings. The summary
// inset reminds the visitor what + when they are booking, in their timezone.
// Implements: architecture.md v1.0 section 5 (POST /api/bookings).

import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import type { BookingType, Slot } from '../api/types.js';
import { formatLongDate, formatTimeRange, shiftInstant, zoneLabel } from '../lib/time.js';
import { validateCustomer, type FieldErrors } from '../lib/validation.js';
import { Banner } from './feedback.js';

export function DetailsForm({
  bookingType,
  slot,
  tz,
  submitting,
  submitError,
  onSubmit,
}: {
  bookingType: BookingType;
  slot: Slot;
  tz: string;
  submitting: boolean;
  submitError?: string;
  onSubmit: (customer: { name: string; email: string; note?: string }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const result = validateCustomer({ name, email, note });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    onSubmit(result.data);
  }

  // Show the ACTUAL meeting start (slot.start + the type's front buffer),
  // consistent with the slot picker and the confirmation. Display only: the
  // parent submits the nominal selectedSlot.start, which this form never touches.
  const frontBufferMin = bookingType.frontBufferMin ?? 0;
  const displayStart = shiftInstant(slot.start, frontBufferMin);

  return (
    <form className="form rise" onSubmit={handleSubmit} noValidate>
      <div className="summary-card">
        <span className="sc-type">{bookingType.name}</span>
        <span className="sc-when">
          {formatLongDate(displayStart, tz)} · {formatTimeRange(displayStart, slot.end, tz)}
        </span>
        <span className="sc-tz">{zoneLabel(tz, displayStart)}</span>
      </div>

      {submitError ? <Banner kind="error">{submitError}</Banner> : null}

      <div className={`field ${errors.name ? 'has-error' : ''}`}>
        <label htmlFor="f-name">
          Name <span className="req">*</span>
        </label>
        <input
          id="f-name"
          name="name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? 'err-name' : undefined}
        />
        {errors.name ? (
          <span className="field-error" id="err-name">
            {errors.name}
          </span>
        ) : null}
      </div>

      <div className={`field ${errors.email ? 'has-error' : ''}`}>
        <label htmlFor="f-email">
          Email <span className="req">*</span>
        </label>
        <input
          id="f-email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? 'err-email' : 'hint-email'}
        />
        {errors.email ? (
          <span className="field-error" id="err-email">
            {errors.email}
          </span>
        ) : (
          <span className="field-hint" id="hint-email">
            Your calendar invite goes here.
          </span>
        )}
      </div>

      <div className={`field ${errors.note ? 'has-error' : ''}`}>
        <label htmlFor="f-note">Anything to add? (optional)</label>
        <textarea
          id="f-note"
          name="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Agenda, context, a question ahead of time…"
          aria-invalid={errors.note ? true : undefined}
          aria-describedby={errors.note ? 'err-note' : undefined}
        />
        {errors.note ? (
          <span className="field-error" id="err-note">
            {errors.note}
          </span>
        ) : null}
      </div>

      <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
        {submitting ? 'Confirming…' : 'Confirm booking'}
      </button>
    </form>
  );
}
